import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { db, storage } from '../firebase/config';
import { collection, getDocs, query } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { API_BASE, getAuthHeaders } from '../config/api';
import './Dashboard.css';

// Component to handle individual album cover loading
function AlbumCard({ album, onUpdateMetadata }) {
  const [coverImageUrl, setCoverImageUrl] = useState(null);
  const [loadingImage, setLoadingImage] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const loadCoverImage = async () => {
      if (!album.coverUrl) {
        setLoadingImage(false);
      return;
    }

      try {
        // The coverUrl is like "/English/Rock/Classic Rock/Beatles/[1968] The Beatles/album.jpg"
        // We need to remove the leading slash and prepend the type (Albums or Singles)
        let storagePath = album.coverUrl;
        if (storagePath.startsWith('/')) {
          storagePath = storagePath.substring(1);
        }
        
        // Prepend the type (Albums or Singles) if not already in the path
        const type = album.type || 'Albums';
        if (!storagePath.startsWith(type + '/')) {
          storagePath = `${type}/${storagePath}`;
        }
        
        const imageRef = ref(storage, storagePath);
        const url = await getDownloadURL(imageRef);
        setCoverImageUrl(url);
    } catch (error) {
        console.error(`Error loading cover for ${album.album}:`, error);
    } finally {
        setLoadingImage(false);
    }
  };

    loadCoverImage();
  }, [album.coverUrl, album.album]);

  return (
    <div className="album-card card">
      <div className="album-cover">
        {loadingImage ? (
          <div className="album-cover-placeholder">
            <span>⏳</span>
          </div>
        ) : coverImageUrl ? (
          <img 
            src={coverImageUrl} 
            alt={album.albumName || album.album}
            onError={(e) => { 
              e.target.style.display = 'none';
              e.target.parentElement.innerHTML = '<div class="album-cover-placeholder"><span>🎵</span></div>';
            }}
          />
        ) : (
          <div className="album-cover-placeholder">
            <span>🎵</span>
          </div>
        )}
      </div>
      <div className="album-info">
        <h3 className="album-title">{album.albumName || album.album || 'Unknown Album'}</h3>
        <p className="album-artist">{album.artistName || album.artist || 'Unknown Artist'}</p>
        <div className="album-meta">
          <span className="album-language">{album.language || '?'}</span>
          <span className="album-genre">{album.genre || '?'}</span>
        </div>
        <p className="album-songs">{album.songs ? album.songs.length : 0} songs</p>
        <button
          className="btn-update-metadata"
          onClick={(e) => {
            e.stopPropagation();
            if (onUpdateMetadata) {
              onUpdateMetadata(album);
            } else {
              console.log('onUpdateMetadata not available', album);
            }
          }}
          disabled={updating || !onUpdateMetadata}
          title="Update metadata from MusicBrainz"
        >
          {updating ? '⏳ Updating...' : '🛠️ Update Metadata'}
        </button>
      </div>
    </div>
  );
}

function Dashboard({ user }) {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ language: 'all', genre: 'all' });
  const [stats, setStats] = useState({ total: 0, languages: {}, genres: {} });
  const [displayLimit, setDisplayLimit] = useState(50); // Only show 50 at a time
  const navigate = useNavigate();
  const location = useLocation();
  
  // Debug: Log location state on every render
  console.log('📊 Dashboard render - location.state:', location.state);
  console.log('📊 Dashboard render - location.pathname:', location.pathname);

  // Metadata update state
  const [showMetadataUpdate, setShowMetadataUpdate] = useState(false);
  const [artistSearch, setArtistSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [matches, setMatches] = useState([]);
  const [selectedMatches, setSelectedMatches] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [updateLog, setUpdateLog] = useState([]);

  const fetchAlbums = useCallback(async () => {
    try {
      console.log('Fetching albums for user:', user.uid);
      console.log('User email:', user.email);
      
      // TEMPORARY: Get ALL albums to see what's available
      const q = query(collection(db, 'albums-flat'));
      
      const querySnapshot = await getDocs(q);
      const fetchedAlbums = [];
      const languageStats = {};
      const genreStats = {};
      
      querySnapshot.forEach((doc) => {
        const albumData = { id: doc.id, ...doc.data() };
        
        // Skip albums with missing files in storage
        if (albumData.storageStatus === 'unavailable') {
          return;
        }
        
        fetchedAlbums.push(albumData);
        
        // Build stats
        const lang = albumData.language || 'Unknown';
        const genre = albumData.genre || 'Unknown';
        languageStats[lang] = (languageStats[lang] || 0) + 1;
        genreStats[genre] = (genreStats[genre] || 0) + 1;
      });
      
      console.log(`Found ${fetchedAlbums.length} albums`);
      console.log('Sample album userIds:', fetchedAlbums.slice(0, 3).map(a => a.userId));
      setAlbums(fetchedAlbums);
      setStats({
        total: fetchedAlbums.length,
        languages: languageStats,
        genres: genreStats
      });
    } catch (error) {
      console.error('Error fetching albums:', error);
      
      // If albums-flat doesn't exist yet, show helpful message
      if (error.code === 'permission-denied' || error.message.includes('albums-flat')) {
        console.log('📝 Note: Run the flattening script first!');
        console.log('   cd tools && npm install && node flatten-albums.js');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchAlbums();
  }, [user, navigate, fetchAlbums]);

  // Handle navigation state for opening metadata update panel
  useEffect(() => {
    const navState = location.state;
    const sessionData = sessionStorage.getItem('metadataUpdate');
    
    console.log('📊 Dashboard useEffect - location.state:', navState);
    console.log('📊 Dashboard useEffect - sessionStorage:', sessionData);
    console.log('📊 Dashboard useEffect - showMetadataUpdate:', showMetadataUpdate);
    
    // Try location.state first, then fallback to sessionStorage
    const shouldOpen = navState?.openMetadataUpdate || (sessionData && JSON.parse(sessionData).open);
    const artistName = navState?.artistName || (sessionData && JSON.parse(sessionData).artistName);
    const albumId = navState?.albumId || (sessionData && JSON.parse(sessionData).albumId);
    const albumName = navState?.albumName || (sessionData && JSON.parse(sessionData).albumName);
    
    if (shouldOpen) {
      console.log('✅ Opening metadata update panel', { artistName, albumId, albumName });
      setShowMetadataUpdate(true);
      
      // Clear sessionStorage after reading
      if (sessionData) {
        sessionStorage.removeItem('metadataUpdate');
      }
      
      // If we have a specific album ID, search for that album only
      if (albumId) {
        console.log('🎯 Searching for specific album:', albumId);
        setArtistSearch(albumName || artistName || '');
        
        const timer = setTimeout(() => {
          const searchSingleAlbum = async () => {
            setSearching(true);
            setMatches([]);
            setSelectedMatches([]);
            setPreviewData(null);
            setUpdateLog([]);
            addUpdateLog(`🔍 Searching for "${albumName || 'album'}"...`, 'info');

            try {
              const headers = await getAuthHeaders();
              const response = await fetch(`${API_BASE}/match-single-album`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ 
                  firestoreId: albumId, 
                  collection: 'JBXX',
                  fromCollection: 'albums-flat' // Albums from LibraryLayout come from albums-flat
                })
              });

              const data = await response.json();
              
              if (data.error) {
                throw new Error(data.error);
              }

              setMatches(data.matches || []);
              const matchedCount = data.matches?.filter(m => m.matched).length || 0;
              addUpdateLog(`✅ Found ${data.matches?.length || 0} potential release(s) (${matchedCount} with high confidence)`, 'success');
              
              if (data.matches && data.matches.length > 0) {
                // Don't auto-select - let user choose the correct match
                // Show all matches so user can review and select the right one
                addUpdateLog(`👁️ Please review and select the correct release from the list below`, 'info');
              }
            } catch (error) {
              addUpdateLog(`❌ Error: ${error.message}`, 'error');
              console.error('Search error:', error);
            } finally {
              setSearching(false);
            }
          };
          searchSingleAlbum();
        }, 500);
        
        return () => clearTimeout(timer);
      } else if (artistName) {
        // Fallback to artist search if no album ID
        console.log('✅ Setting artist search to:', artistName);
        setArtistSearch(artistName);
        
        // Auto-search after a brief delay to ensure state is set
        const timer = setTimeout(() => {
          console.log('🔍 Auto-searching for:', artistName);
          // Use the artist name directly instead of relying on state
          const searchArtist = async () => {
            setSearching(true);
            setMatches([]);
            setSelectedMatches([]);
            setPreviewData(null);
            setUpdateLog([]);
            addUpdateLog(`🔍 Searching for "${artistName}"...`, 'info');

            try {
              const headers = await getAuthHeaders();
              const response = await fetch(`${API_BASE}/match-albums`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ artistName: artistName, collection: 'JBXX' })
              });

              const data = await response.json();
              
              if (data.error) {
                throw new Error(data.error);
              }

              setMatches(data.matches || []);
              addUpdateLog(`✅ Found ${data.matches?.length || 0} matched album(s)`, 'success');
              
              if (data.matches && data.matches.length > 0) {
                // Auto-select all matches
                setSelectedMatches(data.matches.map((_, idx) => idx));
              }
            } catch (error) {
              addUpdateLog(`❌ Error: ${error.message}`, 'error');
              console.error('Search error:', error);
            } finally {
              setSearching(false);
            }
          };
          searchArtist();
        }, 500);
        
        return () => clearTimeout(timer);
      }
      
      // Clear the state so it doesn't reopen on refresh (but only after we've used it)
      if (navState?.openMetadataUpdate) {
        setTimeout(() => {
          navigate(location.pathname, { replace: true, state: {} });
        }, 100);
      }
    }
  }, [location.state, location.pathname]); // Depend on both state and pathname

  const getFilteredAlbums = () => {
    return albums.filter(album => {
      if (filter.language !== 'all' && album.language !== filter.language) return false;
      if (filter.genre !== 'all' && album.genre !== filter.genre) return false;
      return true;
    });
  };

  const handleSearchArtist = async () => {
    if (!artistSearch.trim()) return;
    
    setSearching(true);
    setMatches([]);
    setSelectedMatches([]);
    setPreviewData(null);
    setUpdateLog([]);
    addUpdateLog(`🔍 Searching for "${artistSearch}"...`, 'info');

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/match-albums`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ artistName: artistSearch, collection: 'JBXX' })
      });

      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }

      setMatches(data.matches || []);
      addUpdateLog(`✅ Found ${data.matches?.length || 0} matched album(s)`, 'success');
      
      if (data.matches && data.matches.length > 0) {
        // Auto-select all matches
        setSelectedMatches(data.matches.map((_, idx) => idx));
      }
    } catch (error) {
      addUpdateLog(`❌ Error: ${error.message}`, 'error');
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  const toggleMatch = (index) => {
    setSelectedMatches(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
    setPreviewData(null); // Clear preview when selection changes
  };

  const handlePreviewUpdates = async () => {
    if (selectedMatches.length === 0) return;

    addUpdateLog(`👁️ Loading preview for ${selectedMatches.length} album(s)...`, 'info');
    const previews = [];

    for (const matchIndex of selectedMatches) {
      const match = matches[matchIndex];
      let retries = 3;
      let success = false;
      
      while (retries > 0 && !success) {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
          
          const headers = await getAuthHeaders();
          const response = await fetch(`${API_BASE}/preview-update`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              mbid: match.mbRelease.mbid,
              firestoreId: match.firestoreAlbum.id,
              collection: 'JBXX'
            }),
            signal: controller.signal
          });

          clearTimeout(timeoutId);

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const data = await response.json();
          if (data.error) throw new Error(data.error);

          const matchedSongs = data.preview.songs.filter(s => s.matched).length;
          previews.push({
            album: data.preview.album || { current: match.firestoreAlbum.album },
            songs: data.preview.songs || [],
            songsUpdated: matchedSongs,
            totalSongs: data.preview.songs.length,
            totalTracks: data.totalTracks || data.preview.songs.length
          });
          success = true;
        } catch (error) {
          retries--;
          if (error.name === 'AbortError') {
            addUpdateLog(`⏱️ Timeout previewing ${match.firestoreAlbum.album}${retries > 0 ? `, retrying... (${retries} left)` : ''}`, 'error');
          } else if (error.message.includes('ECONNRESET') || error.message.includes('fetch')) {
            addUpdateLog(`🔌 Connection error previewing ${match.firestoreAlbum.album}${retries > 0 ? `, retrying... (${retries} left)` : ''}`, 'error');
          } else {
            addUpdateLog(`⚠️ Error previewing ${match.firestoreAlbum.album}: ${error.message}${retries > 0 ? `, retrying... (${retries} left)` : ''}`, 'error');
          }
          
          if (retries > 0) {
            // Wait before retrying (exponential backoff)
            await new Promise(resolve => setTimeout(resolve, 1000 * (4 - retries)));
          } else {
            addUpdateLog(`❌ Failed to preview ${match.firestoreAlbum.album} after 3 attempts`, 'error');
          }
        }
      }
    }

    setPreviewData(previews);
    if (previews.length > 0) {
      addUpdateLog(`✅ Preview loaded for ${previews.length} album(s)`, 'success');
    } else {
      addUpdateLog(`⚠️ Could not load preview for any albums. Check server connection.`, 'error');
    }
  };

  const handleCommitUpdates = async () => {
    if (selectedMatches.length === 0) return;
    
    const confirmMessage = `Are you sure you want to update ${selectedMatches.length} album(s)?\n\nThis will:\n- Update DisplayTitle from MusicBrainz\n- Sync changes to albums-flat\n- Preserve original Title (filename) fields`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setUpdating(true);
    addUpdateLog(`🚀 Starting updates for ${selectedMatches.length} album(s)...`, 'info');

    for (const matchIndex of selectedMatches) {
      const match = matches[matchIndex];
      try {
        addUpdateLog(`📝 Updating ${match.firestoreAlbum.album}...`, 'info');
        
        const headers = await getAuthHeaders();
        const response = await fetch(`${API_BASE}/execute-update`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            mbid: match.mbRelease.mbid,
            firestoreId: match.firestoreAlbum.id,
            collection: 'JBXX',
            syncToFlat: true
          })
        });

        const data = await response.json();
        if (data.error) throw new Error(data.error);

        addUpdateLog(`✅ Updated ${match.firestoreAlbum.album} (${data.songsUpdated}/${data.totalSongs} songs)`, 'success');
      } catch (error) {
        addUpdateLog(`❌ Error updating ${match.firestoreAlbum.album}: ${error.message}`, 'error');
      }
    }

    addUpdateLog(`✨ All updates complete!`, 'success');
    setUpdating(false);
    
    // Show success message
    alert(`✅ Successfully updated ${selectedMatches.length} album(s)!\n\nThe album list will refresh to show updated metadata.`);
    
    // Refresh albums list
    setTimeout(() => {
      fetchAlbums();
    }, 1000);
  };

  const addUpdateLog = (message, type = 'info') => {
    setUpdateLog(prev => [...prev, { message, type, timestamp: new Date() }]);
  };

  const handleAlbumMetadataUpdate = async (album) => {
    // When user clicks "Update Metadata" on an album card, search for that artist
    const artistName = album.artist || album.artistName;
    if (!artistName) {
      alert('No artist name found for this album');
      return;
    }

    // Open the metadata update panel and search for this artist
    setShowMetadataUpdate(true);
    setArtistSearch(artistName);
    
    // Auto-search for this artist after state updates
    setTimeout(async () => {
      setSearching(true);
      setMatches([]);
      setSelectedMatches([]);
      setPreviewData(null);
      setUpdateLog([]);
      addUpdateLog(`🔍 Searching for "${artistName}"...`, 'info');

            try {
              // Check if server is running first
              const healthCheck = await fetch(`${API_BASE.replace('/api', '')}/`, { 
                method: 'GET',
                signal: AbortSignal.timeout(5000)
              }).catch(() => null);
              
              if (!healthCheck || !healthCheck.ok) {
                throw new Error('Metadata server is not running. Please start it with: cd tools && npm run gui');
              }

              const headers = await getAuthHeaders();
              const response = await fetch(`${API_BASE}/match-albums`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ artistName, collection: 'JBXX' }),
                signal: AbortSignal.timeout(30000) // 30 second timeout
              });

              if (!response.ok) {
                throw new Error(`Server returned ${response.status}: ${response.statusText}`);
              }

              const data = await response.json();
              
              if (data.error) {
                throw new Error(data.error);
              }

              setMatches(data.matches || []);
              addUpdateLog(`✅ Found ${data.matches?.length || 0} matched album(s)`, 'success');
              
              if (data.matches && data.matches.length > 0) {
                // Auto-select all matches
                setSelectedMatches(data.matches.map((_, idx) => idx));
              }
            } catch (error) {
              let errorMessage = error.message;
              if (error.name === 'AbortError' || error.message.includes('timeout')) {
                errorMessage = 'Request timed out. The server may be slow or unresponsive.';
              } else if (error.message.includes('Failed to fetch') || error.message.includes('ECONNREFUSED')) {
                errorMessage = 'Cannot connect to metadata server. Make sure it\'s running: cd tools && npm run gui';
              }
              addUpdateLog(`❌ Error: ${errorMessage}`, 'error');
              console.error('Search error:', error);
            } finally {
              setSearching(false);
            }
    }, 100);
  };

  const handleQuickUpdate = async (matchIndex) => {
    const match = matches[matchIndex];
    if (!match) return;

    if (!window.confirm(`Update "${match.firestoreAlbum.album}" with metadata from MusicBrainz?`)) {
      return;
    }

    setUpdating(true);
    addUpdateLog(`🚀 Updating ${match.firestoreAlbum.album}...`, 'info');

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/execute-update`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          mbid: match.mbRelease.mbid,
          firestoreId: match.firestoreAlbum.id,
          collection: 'JBXX',
          syncToFlat: true
        })
      });

      const data = await response.json();
      if (data.error) throw new Error(data.error);

      addUpdateLog(`✅ Updated ${match.firestoreAlbum.album} (${data.songsUpdated}/${data.totalSongs} songs)`, 'success');
      alert(`✅ Successfully updated "${match.firestoreAlbum.album}"!\n\n${data.songsUpdated} songs updated with fresh metadata.`);
      
      // Refresh albums list
      setTimeout(() => {
        fetchAlbums();
      }, 1000);
    } catch (error) {
      addUpdateLog(`❌ Error updating ${match.firestoreAlbum.album}: ${error.message}`, 'error');
      alert(`❌ Error: ${error.message}`);
    } finally {
      setUpdating(false);
    }
  };

  if (!user) {
    return null;
  }

  const filteredAlbums = getFilteredAlbums();

  return (
    <div className="dashboard">
      <div className="container">
        <div className="dashboard-header">
          <h1>Welcome, {user.email}!</h1>
          <p>Your Music Library - {stats.total} Albums</p>
        </div>

        <div className="dashboard-content">
          {/* Stats Section */}
          <div className="card stats-card">
            <h2>📊 Library Stats</h2>
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-label">Total Albums:</span>
                <span className="stat-value">{stats.total}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Languages:</span>
                <span className="stat-value">{Object.keys(stats.languages).length}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Genres:</span>
                <span className="stat-value">{Object.keys(stats.genres).length}</span>
              </div>
            </div>
          </div>

          {/* Metadata Update Section */}
          <div className="card tools-card">
            <h2>🛠️ Update Metadata from MusicBrainz</h2>
            {!showMetadataUpdate ? (
              <div style={{ textAlign: 'center', padding: '1rem' }}>
                <p style={{ marginBottom: '1rem', opacity: 0.8 }}>
                  Search for an artist to update album metadata from MusicBrainz
                </p>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowMetadataUpdate(true)}
                >
                  🎵 Update Metadata
                </button>
              </div>
            ) : (
              <div className="metadata-update-panel">
                <div className="search-section">
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '1rem' }}>
                    <input
                      type="text"
                      placeholder="Enter artist name (e.g., Los Angeles Negros)"
                      value={artistSearch}
                      onChange={(e) => setArtistSearch(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearchArtist()}
                      style={{
                        flex: 1,
                        padding: '10px',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '1em'
                      }}
                    />
                    <select
                      value="JBXX"
                      style={{
                        padding: '10px',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '1em'
                      }}
                    >
                      <option value="JBXX">JBXX</option>
                      <option value="albums-flat">albums-flat</option>
                    </select>
                    <button
                      className="btn btn-primary"
                      onClick={handleSearchArtist}
                      disabled={searching || !artistSearch.trim()}
                    >
                      {searching ? '🔍 Searching...' : '🔍 Search'}
                    </button>
                    <button
                      className="btn"
                      onClick={() => {
                        setShowMetadataUpdate(false);
                        setMatches([]);
                        setSelectedMatches([]);
                        setPreviewData(null);
                        setUpdateLog([]);
                      }}
                      style={{ background: '#f5f5f5' }}
                    >
                      ✕ Close
                    </button>
                  </div>
                </div>

                {matches.length > 0 && (
                  <div className="matches-section">
                    <h3>📊 Matched Albums ({matches.length})</h3>
                    <div className="matches-list">
                      {matches.map((match, index) => (
                        <div
                          key={index}
                          className={`match-item ${selectedMatches.includes(index) ? 'selected' : ''}`}
                          onClick={() => toggleMatch(index)}
                        >
                          <input
                            type="checkbox"
                            checked={selectedMatches.includes(index)}
                            onChange={() => toggleMatch(index)}
                            style={{ marginRight: '10px', cursor: 'pointer' }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '600', display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <span>{match.firestoreAlbum.album}</span>
                              <span className={`confidence-badge confidence-${match.confidence}`}>
                                {match.confidence}%
                              </span>
                              {match.mbRelease.type && (
                                <span style={{ 
                                  fontSize: '0.75em', 
                                  padding: '2px 6px', 
                                  background: match.mbRelease.type === 'Album' ? '#4caf50' : match.mbRelease.type === 'Single' ? '#ff9800' : '#2196f3',
                                  color: 'white',
                                  borderRadius: '4px',
                                  fontWeight: '500'
                                }}>
                                  {match.mbRelease.type === 'Album' ? '💿' : match.mbRelease.type === 'Single' ? '📀' : '📄'} {match.mbRelease.type}
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
                              MB: "{match.mbRelease.album}" ({match.mbRelease.year || '?'})
                            </div>
                          </div>
                          {selectedMatches.includes(index) && (
                            <button
                              className="btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleQuickUpdate(index);
                              }}
                              disabled={updating}
                              style={{
                                padding: '6px 12px',
                                fontSize: '0.85em',
                                background: '#4caf50',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: '600'
                              }}
                              title="Quick update this album"
                            >
                              ⚡ Update
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#e3f2fd', borderRadius: '8px', border: '2px solid #667eea' }}>
                      <div style={{ marginBottom: '1rem', fontWeight: '600', color: '#333' }}>
                        {selectedMatches.length > 0 
                          ? `✅ ${selectedMatches.length} album(s) selected for update`
                          : '⚠️ Select at least one album to update'}
                      </div>
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <button
                          className="btn btn-primary"
                          onClick={handlePreviewUpdates}
                          disabled={updating || selectedMatches.length === 0}
                          style={{ 
                            padding: '12px 24px',
                            fontSize: '1em',
                            fontWeight: '600',
                            minWidth: '180px'
                          }}
                        >
                          {updating ? '⏳ Processing...' : '👁️ Preview Changes'}
                        </button>
                        {previewData && (
                          <button
                            className="btn btn-success"
                            onClick={handleCommitUpdates}
                            disabled={updating}
                            style={{ 
                              marginLeft: '0',
                              background: '#4caf50',
                              padding: '12px 24px',
                              fontSize: '1em',
                              fontWeight: '600',
                              minWidth: '180px',
                              color: 'white'
                            }}
                          >
                            {updating ? '⏳ Updating...' : '✅ Commit Updates'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {previewData && (
                  <div className="preview-section" style={{ marginTop: '1.5rem', padding: '1rem', background: '#f9f9f9', borderRadius: '8px' }}>
                    <h3>👁️ Preview Changes</h3>
                    <div style={{ marginBottom: '1rem', padding: '10px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', fontSize: '0.9em' }}>
                      ⚠️ Review the changes below before committing. This will update the {selectedMatches.length} selected album(s) in Firestore.
                    </div>
                    {previewData.map((preview, idx) => (
                      <div key={idx} style={{ marginBottom: '1.5rem', padding: '15px', border: '1px solid #e0e0e0', borderRadius: '6px', background: 'white' }}>
                        <div style={{ fontWeight: '600', marginBottom: '10px', color: '#333', fontSize: '1.1em' }}>
                          {preview.album?.current || preview.album}
                        </div>
                        {preview.album?.new && preview.album.new !== preview.album.current && (
                          <div style={{ fontSize: '0.9em', color: '#666', marginBottom: '10px' }}>
                            Album name: <span style={{ textDecoration: 'line-through', color: '#999' }}>{preview.album.current}</span> → <strong>{preview.album.new}</strong>
                          </div>
                        )}
                        <div style={{ fontSize: '0.9em', color: '#666', marginBottom: '15px' }}>
                          Songs to update: <strong>{preview.songsUpdated}</strong>/{preview.totalSongs}
                          {preview.songsUpdated > 0 && (
                            <span style={{ color: '#4caf50', marginLeft: '10px' }}>✓ Ready to update</span>
                          )}
                        </div>
                        
                        {/* Track-by-track comparison */}
                        {preview.songs && preview.songs.length > 0 && (
                          <div style={{ marginTop: '15px' }}>
                            <div style={{ fontWeight: '600', marginBottom: '10px', fontSize: '0.95em', color: '#555' }}>
                              Track Changes:
                            </div>
                            <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '10px', background: '#fafafa' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85em' }}>
                                <thead>
                                  <tr style={{ borderBottom: '2px solid #ddd', background: '#f0f0f0' }}>
                                    <th style={{ padding: '8px', textAlign: 'left', width: '50px' }}>#</th>
                                    <th style={{ padding: '8px', textAlign: 'left', width: '40%' }}>Current Title</th>
                                    <th style={{ padding: '8px', textAlign: 'left', width: '40%' }}>New Title</th>
                                    <th style={{ padding: '8px', textAlign: 'center', width: '80px' }}>Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {preview.songs.map((song, songIdx) => (
                                    <tr 
                                      key={songIdx} 
                                      style={{ 
                                        borderBottom: '1px solid #eee',
                                        background: song.matched ? (song.currentDisplayTitle !== song.newDisplayTitle ? '#fff9e6' : '#f0f8f0') : '#ffe6e6'
                                      }}
                                    >
                                      <td style={{ padding: '8px', color: '#666' }}>{song.trackNumber || (songIdx + 1)}</td>
                                      <td style={{ padding: '8px' }}>
                                        <span style={{ color: song.matched && song.currentDisplayTitle !== song.newDisplayTitle ? '#d32f2f' : '#666' }}>
                                          {song.currentDisplayTitle || song.title || 'N/A'}
                                        </span>
                                      </td>
                                      <td style={{ padding: '8px' }}>
                                        {song.matched ? (
                                          <span style={{ color: song.currentDisplayTitle !== song.newDisplayTitle ? '#2e7d32' : '#666', fontWeight: song.currentDisplayTitle !== song.newDisplayTitle ? '500' : 'normal' }}>
                                            {song.newDisplayTitle || 'N/A'}
                                          </span>
                                        ) : (
                                          <span style={{ color: '#999', fontStyle: 'italic' }}>No match</span>
                                        )}
                                      </td>
                                      <td style={{ padding: '8px', textAlign: 'center' }}>
                                        {song.matched ? (
                                          song.currentDisplayTitle !== song.newDisplayTitle ? (
                                            <span style={{ color: '#ff9800', fontWeight: '600' }}>✏️</span>
                                          ) : (
                                            <span style={{ color: '#4caf50' }}>✓</span>
                                          )
                                        ) : (
                                          <span style={{ color: '#f44336' }}>✗</span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            <div style={{ marginTop: '10px', fontSize: '0.8em', color: '#666', fontStyle: 'italic' }}>
                              Legend: <span style={{ color: '#ff9800' }}>✏️</span> = Will change, <span style={{ color: '#4caf50' }}>✓</span> = Matched (no change), <span style={{ color: '#f44336' }}>✗</span> = No match
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {updateLog.length > 0 && (
                  <div className="update-log" style={{ marginTop: '1rem', padding: '1rem', background: '#1e1e1e', color: '#d4d4d4', borderRadius: '8px', maxHeight: '200px', overflowY: 'auto', fontFamily: 'monospace', fontSize: '0.9em' }}>
                    {updateLog.map((log, idx) => (
                      <div key={idx} style={{ marginBottom: '5px', color: log.type === 'success' ? '#4caf50' : log.type === 'error' ? '#f44336' : '#d4d4d4' }}>
                        [{new Date().toLocaleTimeString()}] {log.message}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Filter Section */}
          <div className="card filter-card">
            <h2>🔍 Filter Albums</h2>
            <div className="filter-controls">
              <div className="filter-group">
                <label>Language:</label>
                <select 
                  value={filter.language} 
                  onChange={(e) => setFilter({...filter, language: e.target.value})}
                  className="filter-select"
                >
                  <option value="all">All Languages ({stats.total})</option>
                  {Object.entries(stats.languages).sort().map(([lang, count]) => (
                    <option key={lang} value={lang}>{lang} ({count})</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Genre:</label>
                <select 
                  value={filter.genre} 
                  onChange={(e) => setFilter({...filter, genre: e.target.value})}
                  className="filter-select"
                >
                  <option value="all">All Genres ({stats.total})</option>
                  {Object.entries(stats.genres).sort().map(([genre, count]) => (
                    <option key={genre} value={genre}>{genre} ({count})</option>
                  ))}
                </select>
              </div>
            </div>
            <p className="filter-results">Showing {filteredAlbums.length} of {stats.total} albums</p>
          </div>

          {/* Albums Section */}
          <div className="albums-section">
            <h2>🎵 Your Albums</h2>
            {loading ? (
              <p>Loading your music library...</p>
            ) : albums.length === 0 ? (
              <div className="empty-state">
                <p>No albums found for your account.</p>
                <p>Make sure you've run the flattening script!</p>
              </div>
            ) : filteredAlbums.length === 0 ? (
              <div className="empty-state">
                <p>No albums match your filters. Try different options!</p>
              </div>
            ) : (
              <>
                <div className="albums-grid">
                  {filteredAlbums.slice(0, displayLimit).map((album) => (
                    <AlbumCard 
                      key={album.id} 
                      album={album}
                      onUpdateMetadata={handleAlbumMetadataUpdate}
                    />
                  ))}
                </div>
                {filteredAlbums.length > displayLimit && (
                  <div style={{ textAlign: 'center', margin: '2rem 0' }}>
                    <button
                      className="btn btn-primary" 
                      onClick={() => setDisplayLimit(prev => prev + 50)}
                    >
                      Load More Albums ({displayLimit} of {filteredAlbums.length} shown)
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;

